import { Icon, IconsEnum, toIconsEnum } from 'components/Icons';
import { useTranslation } from 'hooks/useLingUI';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { ItemWithTranslation } from 'types/swagger';
import { isServerSide } from 'utility/constant';
import { isCORS, isFrame, isSeamless, isTruthy } from 'utility/functions';
import { MessageCategory, MessageCategoryType } from '.';
import styles from './SimpleMessage.module.scss';

const isCORSDocument = isCORS();
const isFrameDocument = isFrame();
const isSeamlessDocument = isSeamless();
const isBackButtonEnabled = isSeamlessDocument || isCORSDocument ? false : true;

export interface SimpleMessageProps extends ItemWithTranslation<{ message?: string }> {
  category: MessageCategoryType | MessageCategory;
  backLink?: string;
}

export const SimpleMessage = ({ category, message, messageTrKey, backLink }: SimpleMessageProps) => {
  const { t } = useTranslation();
  const { push } = useRouter();

  const navigate = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault();
      e.stopPropagation();
      if (isServerSide) return;
      if (isFrameDocument) {
        window!.top!.location!.replace(backLink!);
      } else {
        push(backLink!);
      }
    },
    [backLink, push]
  );

  const iconId = useMemo((): IconsEnum => toIconsEnum(`simplemessage-${category}`), [category]);

  return (
    <div className={styles.container} data-destid={category} suppressHydrationWarning>
      {category !== MessageCategory.none && <Icon iconId={iconId} />}
      <div className={styles.message} suppressHydrationWarning>
        {t(messageTrKey, message)}
      </div>
      {isBackButtonEnabled && isTruthy(backLink?.length) && (
        <a className={styles.cta} href={backLink} onClick={navigate} suppressHydrationWarning>
          {t('back-to-home')}
        </a>
      )}
    </div>
  );
};
export const TwoZeroZero = () => (
  <SimpleMessage category={MessageCategory.success} message="200 - Success" messageTrKey={'200'} />
);
export const FourZeroOne = () => (
  <SimpleMessage
    category={MessageCategory.information}
    message="401 - Unauthorized"
    messageTrKey={'401'}
    backLink={process.env.NEXT_PUBLIC_BASE_URL ?? '/'}
  />
);
export const FourZeroThree = () => (
  <SimpleMessage
    category={MessageCategory.information}
    message="403 - Forbidden"
    messageTrKey={'403'}
    backLink={process.env.NEXT_PUBLIC_BASE_URL ?? '/'}
  />
);
export const FourZeroFour = () => (
  <SimpleMessage
    category={MessageCategory.error}
    message="404 - Not Found"
    messageTrKey={'404'}
    backLink={process.env.NEXT_PUBLIC_BASE_URL ?? '/'}
  />
);
export const FiveZeroZero = () => (
  <SimpleMessage
    category={MessageCategory.error}
    message="500 - Server Error"
    messageTrKey={'500'}
    backLink={process.env.NEXT_PUBLIC_BASE_URL ?? '/'}
  />
);
